import React, { useState, useMemo, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useCourse } from "../../../../context/CourseContext";
import {
  card,
  microLabel,
  bodySecondary,
  accentBarSection,
  accentBarCard,
  motionFadeUp,
  motionStaggerChild,
  pageTitle,
  emptyState,
  dividerInner,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";
import DateRangePicker from "../../../../components/common/DateRangePicker";
import { subMonths, isWithinInterval, parseISO, startOfDay, endOfDay } from "date-fns";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  FileSpreadsheet,
  Users,
  AlertCircle,
  Check,
  X,
  Calendar,
  Percent,
  Filter,
  Search,
  Download,
} from "lucide-react";

const AttendanceStats = () => {
  // Get data from the course context
  const { courseData, savedSessions, markStudentPresent, markStudentAbsent, getSessionKey } = useCourse();

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("all");
  const [selectedBranch, setSelectedBranch] = useState("");

  // Date range filter - default to last 6 months
  const [dateRange, setDateRange] = useState({
    startDate: subMonths(new Date(), 6),
    endDate: new Date(),
  });

  // Handle date range change
  const handleDateRangeChange = ({ startDate, endDate }) => {
    setDateRange({ startDate, endDate });
  };

  // Pagination states for students (rows)
  const [studentPage, setStudentPage] = useState(1);
  const [studentsPerPage, setStudentsPerPage] = useState(100);

  // Extract unique programs from students
  const uniquePrograms = useMemo(() => {
    if (!courseData?.students) return [];
    const programs = [...new Set(courseData.students.map((s) => s.program).filter(Boolean))];
    return programs.sort();
  }, [courseData?.students]);

  // Check if we need to show program filter (only if multiple programs exist)
  const showProgramFilter = uniquePrograms.length > 1;

  // Extract unique branches based on selected program
  const uniqueBranches = useMemo(() => {
    if (!courseData?.students) return [];
    let studentsToFilter = courseData.students;

    if (selectedProgram !== "all") {
      studentsToFilter = studentsToFilter.filter((s) => s.program === selectedProgram);
    }

    const branches = [...new Set(studentsToFilter.map((s) => s.branch).filter(Boolean))];
    return branches.sort();
  }, [courseData?.students, selectedProgram]);

  // Auto-select first branch when branches change or when not yet selected
  useEffect(() => {
    if (uniqueBranches.length > 0 && (!selectedBranch || !uniqueBranches.includes(selectedBranch))) {
      setSelectedBranch(uniqueBranches[0]);
    }
  }, [uniqueBranches, selectedBranch]);

  // Whether to show the branch selector (hide if only one branch)
  const showBranchSelector = uniqueBranches.length > 1;

  // Reset student page when filters change
  useEffect(() => {
    setStudentPage(1);
  }, [searchTerm, selectedProgram, selectedBranch, studentsPerPage]);

  // Filter and sort students
  const filteredStudents = useMemo(() => {
    if (!courseData?.students) return [];

    const filtered = courseData.students.filter((student) => {
      // Search filter
      const matchesSearch = searchTerm === "" ||
        student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.rollNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email?.toLowerCase().includes(searchTerm.toLowerCase());

      // Program filter
      const matchesProgram = selectedProgram === "all" || student.program === selectedProgram;

      // Branch filter — always filter by selected branch
      const matchesBranch = !selectedBranch || student.branch === selectedBranch;

      return matchesSearch && matchesProgram && matchesBranch;
    });

    // Sort by rollNo in ascending order
    return filtered.sort((a, b) => {
      const rollA = a.rollNo || "";
      const rollB = b.rollNo || "";
      return rollA.localeCompare(rollB, undefined, { numeric: true });
    });
  }, [courseData?.students, searchTerm, selectedProgram, selectedBranch]);

  // Pagination calculations for students
  const studentIndexOfLastItem = studentPage * studentsPerPage;
  const studentIndexOfFirstItem = studentIndexOfLastItem - studentsPerPage;
  const currentStudents = filteredStudents.slice(studentIndexOfFirstItem, studentIndexOfLastItem);
  const totalStudentPages = Math.ceil(filteredStudents.length / studentsPerPage);

  // Ref for the scrollable container
  const scrollContainerRef = useRef(null);

  // Extract only saved sessions (from backend) filtered by selected branch and date range - chronological order
  const allSessionsArray = Array.isArray(courseData.attendance?.sessions) ? courseData.attendance.sessions : [];
  const sessionDates = useMemo(() => {
    return allSessionsArray
      .filter(session => {
        // Must be a saved session
        if (!savedSessions.has(getSessionKey(session))) return false;
        // Filter by selected branch
        if (selectedBranch && session.branch !== selectedBranch) return false;

        // Filter by date range
        try {
          const dateStr = (session.date || "").includes("T")
            ? session.date.split("T")[0]
            : session.date;
          const sessionDate = parseISO(dateStr);

          if (!isWithinInterval(sessionDate, {
            start: startOfDay(dateRange.startDate),
            end: endOfDay(dateRange.endDate)
          })) {
            return false;
          }
        } catch (e) {
          // If date parsing fails, include the session
        }

        return true;
      })
      .sort((a, b) => {
        const keyA = getSessionKey(a);
        const keyB = getSessionKey(b);
        return keyA.localeCompare(keyB);
      });
  }, [allSessionsArray, savedSessions, selectedBranch, dateRange, getSessionKey]);

  // Auto-scroll to the end (latest sessions) on mount
  useEffect(() => {
    if (scrollContainerRef.current && sessionDates.length > 0) {
      setTimeout(() => {
        scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
      }, 100);
    }
  }, [sessionDates.length]);

  // Scroll navigation handlers
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // Format session for display (e.g. "10 Feb 18:00")
  const formatDate = (session) => {
    const dateStr = (session.date || "").includes("T")
      ? session.date.split("T")[0]
      : session.date;
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    return `${day} ${month} ${session.time || ""}`;
  };

  // Calculate attendance percentage for each student
  const calculateAttendance = (studentId) => {
    let presentCount = 0;

    sessionDates.forEach((session) => {
      if ((session.attendees || []).includes(studentId)) {
        presentCount++;
      }
    });

    return sessionDates.length > 0
      ? Math.round((presentCount / sessionDates.length) * 100)
      : 0;
  };

  // Get status color based on attendance percentage
  const getStatusColor = (percentage) => {
    if (percentage >= 75) return "text-accent1";
    if (percentage >= 50) return "text-amber-500 dark:text-amber-400";
    return "text-red-500 dark:text-red-400";
  };

  // Toggle attendance for a student in a session
  const toggleAttendance = (session, studentId, isCurrentlyPresent) => {
    const dateStr = (session.date || "").includes("T")
      ? session.date.split("T")[0]
      : session.date;

    if (isCurrentlyPresent) {
      markStudentAbsent(dateStr, session.time, session.branch || "", studentId);
    } else {
      markStudentPresent(dateStr, session.time, session.branch || "", studentId);
    }
  };

  const exportToExcel = () => {
    const exportData = filteredStudents.map((student, index) => {
      const row = {
        "Sl.No": index + 1,
        "Roll No": student.rollNo || "—",
        "Name": student.name || "—",
        "Branch": student.branch || "—",
        "Attendance %": calculateAttendance(student.id) + "%",
      };
      sessionDates.forEach((session) => {
        const isPresent = (session.attendees || []).includes(student.id);
        row[formatDate(session)] = isPresent ? "P" : "A";
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");

    const branchSuffix = selectedBranch ? `_${selectedBranch}` : "";
    XLSX.writeFile(workbook, `Attendance_Status${branchSuffix}.xlsx`);
    toast.success("Attendance exported successfully!");
  };

  return (
    <motion.div {...motionFadeUp} className="p-4 sm:p-6 lg:p-8 space-y-8">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className={pageTitle}>
            Attendance Status Sheet
          </h1>
          <p className={cn(bodySecondary, "mt-1")}>
            {selectedBranch
              ? `Showing attendance for ${selectedBranch} branch`
              : "Click on any attendance cell to toggle present/absent status"}
          </p>
          <div className={cn(accentBarSection, "mt-3")} />
        </div>
        <button
          onClick={exportToExcel}
          disabled={sessionDates.length === 0}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Download className="w-4 h-4" />
          Export to Excel
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          {
            label: selectedBranch ? `Sessions (${selectedBranch})` : "Total Sessions",
            value: sessionDates.length,
          },
          {
            label: selectedBranch ? `Students (${selectedBranch})` : "Total Students",
            value: filteredStudents.length,
            suffix: filteredStudents.length !== courseData.students?.length ? (
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">
                / {courseData.students?.length || 0}
              </span>
            ) : null,
          },
          {
            label: selectedBranch ? `Average (${selectedBranch})` : "Class Average",
            value: filteredStudents.length > 0
              ? Math.round(
                  filteredStudents.reduce(
                    (sum, student) => sum + calculateAttendance(student.id),
                    0
                  ) / filteredStudents.length
                )
              : 0,
            suffix: "%",
          },
        ].map((stat, i) => (
          <motion.div key={i} {...motionStaggerChild(i)} className={cn(card, "p-6")}>
            <div className={accentBarCard} />
            <div className="mt-4">
              <h3 className={microLabel}>
                {stat.label}
              </h3>
              <p className="text-3xl font-black tracking-tight text-accent1 mt-1">
                {stat.value}
                {stat.suffix}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {sessionDates.length === 0 ? (
        <div className={cn(emptyState, "py-12")}>
          <FileSpreadsheet className="h-10 w-10 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-black tracking-tight text-gray-800 dark:text-white mb-2">
            No Attendance Records
          </h3>
          <p className={cn(bodySecondary, "mb-6 max-w-md mx-auto")}>
            Start recording attendance to see detailed statistics. You can mark
            attendance from the "Mark Attendance" section.
          </p>

          {/* Show branch selector when no records */}
          {showBranchSelector && (
            <div className="flex items-center justify-center gap-3">
              <label className={cn(bodySecondary, "font-medium")}>
                Try another branch:
              </label>
              <div className="relative w-48">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Filter className="w-4 h-4 text-accent1" />
                </div>
                <select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  className="pl-9 pr-8 py-2 w-full border-2 border-accent1 rounded-sm focus:ring-2 focus:ring-accent1/50 focus:border-transparent bg-accent1/5 dark:bg-accent1/10 text-accent1 font-medium appearance-none cursor-pointer text-sm"
                >
                  {uniqueBranches.map((branch) => (
                    <option key={branch} value={branch}>
                      {branch}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                  <ChevronDown className="w-4 h-4 text-accent1" />
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={card}>
          <div className={cn("p-6 border-b", dividerInner)}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center space-x-3">
                <FileSpreadsheet className="w-5 h-5 text-accent1" />
                <h2 className="text-xl font-black tracking-tight text-gray-800 dark:text-white">
                  Attendance Records
                </h2>
              </div>

              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                {/* Date Range Picker */}
                <DateRangePicker
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  onDateChange={handleDateRangeChange}
                />

                {/* Search Input */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search students..."
                    className="pl-9 pr-4 py-2 w-full sm:w-48 border border-gray-200 dark:border-gray-700 rounded-sm focus:ring-2 focus:ring-accent1/50 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {/* Program Filter - Only show if multiple programs exist */}
                {showProgramFilter && (
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Filter className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    </div>
                    <select
                      value={selectedProgram}
                      onChange={(e) => setSelectedProgram(e.target.value)}
                      className="pl-9 pr-8 py-2 border border-gray-200 dark:border-gray-700 rounded-sm focus:ring-2 focus:ring-accent1/50 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white appearance-none cursor-pointer text-sm min-w-[130px]"
                    >
                      <option value="all">All Programs</option>
                      {uniquePrograms.map((program) => (
                        <option key={program} value={program}>
                          {program}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                      <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    </div>
                  </div>
                )}

                {/* Branch Filter — shown only when multiple branches exist */}
                {showBranchSelector && (
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Filter className="w-4 h-4 text-accent1" />
                    </div>
                    <select
                      value={selectedBranch}
                      onChange={(e) => setSelectedBranch(e.target.value)}
                      className="pl-9 pr-8 py-2 border-2 border-accent1 rounded-sm focus:ring-2 focus:ring-accent1/50 focus:border-transparent bg-accent1/5 dark:bg-accent1/10 text-accent1 font-medium appearance-none cursor-pointer text-sm min-w-[130px]"
                    >
                      {uniqueBranches.map((branch) => (
                        <option key={branch} value={branch}>
                          {branch}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                      <ChevronDown className="w-4 h-4 text-accent1" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Session Navigation */}
          {sessionDates.length > 0 && (
            <div className={cn("flex items-center justify-between px-6 py-3 bg-gray-50 dark:bg-gray-900 border-b", dividerInner)}>
              <div className={bodySecondary}>
                {sessionDates.length} session{sessionDates.length !== 1 ? 's' : ''} in selected date range
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={scrollLeft}
                  className="p-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
                  title="Scroll to older sessions"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className={cn(bodySecondary, "px-2")}>
                  Scroll sessions
                </span>
                <button
                  onClick={scrollRight}
                  className="p-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
                  title="Scroll to newer sessions"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Table with frozen columns */}
          <div className="relative">
            <div
              ref={scrollContainerRef}
              className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#d1d5db transparent'
              }}
            >
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900">
                    {/* Frozen columns */}
                    <th className={cn("sticky left-0 z-20 bg-gray-50 dark:bg-gray-900 p-3 text-left border-b border-r min-w-[60px]", microLabel, dividerInner)}>
                      Sl. No.
                    </th>
                    <th className={cn("sticky left-[60px] z-20 bg-gray-50 dark:bg-gray-900 p-3 text-left border-b border-r min-w-[100px]", microLabel, dividerInner)}>
                      Roll No.
                    </th>
                    <th className={cn("sticky left-[160px] z-20 bg-gray-50 dark:bg-gray-900 p-3 text-left border-b border-r min-w-[150px]", microLabel, dividerInner)}>
                      Student Name
                    </th>
                    <th className={cn("sticky left-[310px] z-20 bg-gray-50 dark:bg-gray-900 p-3 text-center border-b border-r-2 min-w-[90px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]", microLabel, dividerInner)}>
                      <div className="flex items-center justify-center">
                        <span>Attendance</span> <Percent className="w-4 h-4 ml-1" />
                      </div>
                    </th>
                    {/* Scrollable session columns */}
                    {sessionDates.map((session, sIdx) => (
                      <th
                        key={getSessionKey(session) || sIdx}
                        className={cn("p-3 text-center whitespace-nowrap border-b min-w-[100px]", microLabel, dividerInner)}
                      >
                        {formatDate(session)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentStudents.length === 0 ? (
                    <tr>
                      <td colSpan={4 + sessionDates.length} className={cn("py-8 text-center", bodySecondary)}>
                        No students found matching the filters
                      </td>
                    </tr>
                  ) : (
                    currentStudents.map((student, index) => {
                      const attendancePercentage = calculateAttendance(student.id);
                      const statusColor = getStatusColor(attendancePercentage);

                      return (
                        <tr
                          key={student.id}
                          className={cn("border-b hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150", dividerInner)}
                        >
                          {/* Frozen columns */}
                          <td className={cn("sticky left-0 z-10 bg-white dark:bg-gray-800 p-3 text-gray-900 dark:text-white border-r", dividerInner)}>
                            {studentIndexOfFirstItem + index + 1}
                          </td>
                          <td className={cn("sticky left-[60px] z-10 bg-white dark:bg-gray-800 p-3 text-gray-900 dark:text-white font-medium border-r", dividerInner)}>
                            {student?.rollNo || "—"}
                          </td>
                          <td className={cn("sticky left-[160px] z-10 bg-white dark:bg-gray-800 p-3 text-gray-900 dark:text-white font-medium border-r max-w-[150px] truncate", dividerInner)}>
                            {student?.name}
                          </td>
                          <td className={cn("sticky left-[310px] z-10 bg-white dark:bg-gray-800 p-3 border-r-2 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]", dividerInner)}>
                            <div className="flex items-center justify-center">
                              <div className={`font-bold ${statusColor}`}>
                                {attendancePercentage}%
                              </div>
                            </div>
                          </td>
                          {/* Scrollable session columns */}
                          {sessionDates.map((session, sIdx) => {
                            const isPresent = (session.attendees || []).includes(student.id);
                            return (
                              <td key={getSessionKey(session) || sIdx} className="p-2 text-center">
                                <div className="flex justify-center">
                                  <button
                                    onClick={() => toggleAttendance(session, student.id, isPresent)}
                                    className={`w-8 h-8 rounded-sm flex items-center justify-center transition-all duration-150 hover:scale-110 cursor-pointer ${
                                      isPresent
                                        ? "bg-accent1/10 text-accent1 hover:bg-accent1/20"
                                        : "bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50"
                                    }`}
                                    title={isPresent ? "Click to mark absent" : "Click to mark present"}
                                  >
                                    {isPresent ? (
                                      <Check className="w-5 h-5" />
                                    ) : (
                                      <X className="w-5 h-5" />
                                    )}
                                  </button>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          <div className={cn("p-4 border-t", dividerInner)}>
            {filteredStudents.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className={bodySecondary}>
                    Showing {filteredStudents.length > 0 ? studentIndexOfFirstItem + 1 : 0} to{" "}
                    {Math.min(studentIndexOfLastItem, filteredStudents.length)} of{" "}
                    {filteredStudents.length} students
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={bodySecondary}>Show:</span>
                    <select
                      value={studentsPerPage}
                      onChange={(e) => setStudentsPerPage(Number(e.target.value))}
                      className="px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm cursor-pointer"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setStudentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={studentPage === 1}
                    className="p-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className={cn(bodySecondary, "px-2")}>
                    Page {studentPage} of {totalStudentPages || 1}
                  </span>
                  <button
                    onClick={() => setStudentPage((prev) => Math.min(prev + 1, totalStudentPages))}
                    disabled={studentPage === totalStudentPages || totalStudentPages === 0}
                    className="p-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="flex items-start p-5 rounded-sm bg-accent1/5 dark:bg-accent1/10 border border-accent1/20 dark:border-accent1/30 mt-6">
        <AlertCircle className="w-5 h-5 text-accent1 mt-0.5 mr-3 flex-shrink-0" />
        <div>
          <p className="text-accent1 font-bold mb-1">
            About Attendance Status
          </p>
          <p className={bodySecondary}>
            Students with attendance below 75% may not be eligible for exams.
            The attendance percentage is calculated based on sessions
            conducted for the selected branch. Use the navigation arrows or scroll to view all sessions.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default AttendanceStats;
